import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  X,
  Target,
  Calendar,
  User,
  MessageSquare,
  FileText,
  ChevronRight
} from 'lucide-react';
import { OnboardingProject, OnboardingTask } from './types';
import { SOMAChatInterface } from './SOMAChatInterface';
import { ClientHealth } from '../../SupportBridge/Overview/ClientHealth';

interface ClientViewProps {
  project: OnboardingProject;
  tasks: OnboardingTask[];
  onTaskUpdate?: (taskId: string, status: string) => void;
}

export const ClientView: React.FC<ClientViewProps> = ({ 
  project, 
  tasks, 
  onTaskUpdate 
}) => {
  const [selectedTask, setSelectedTask] = useState<OnboardingTask | null>(null);
  const [showSomaChat, setShowSomaChat] = useState(false);

  const completedTasks = tasks.filter(t => t.status === 'completed');
  const inProgressTasks = tasks.filter(t => t.status === 'in-progress');
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const overdueTasks = tasks.filter(t => t.status === 'overdue');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in-progress': return <Clock className="w-5 h-5 text-blue-500" />;
      case 'pending': return <Clock className="w-5 h-5 text-gray-400" />;
      case 'overdue': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'border-green-200 bg-white text-green-700';
      case 'in-progress': return 'border-blue-200 bg-white text-blue-700';
      case 'pending': return 'border-gray-200 bg-white text-gray-700';
      case 'overdue': return 'border-red-200 bg-white text-red-700';
      default: return 'border-gray-200 bg-white text-gray-700';
    }
  };

  return (
    <div className="space-y-8">
      {/* Premium Client Header Card */}
      <div className="p-8 rounded-xl bg-white border border-gray-200 shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 space-y-6 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-blue-600 font-bold text-lg drop-shadow-sm">TC</span>
              </div>
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">{project.name}</h1>
              <p className="text-gray-600 text-lg">Client: {project.clientName}</p>
            </div>
          </div>
          <div className="text-left lg:text-right">
            <p className="text-sm text-gray-500 mb-1">Target Completion</p>
            <p className="text-xl font-bold text-gray-900">
              {new Date(project.targetDate).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Client Health Stoplight */}
        {(import.meta.env.VITE_FEATURE_CLIENT_HEALTH !== 'false') && (
          <div className="mb-6">
            <ClientHealth projectId={project.id} className="mb-3" />
          </div>
        )}

        {/* Enhanced Progress Overview */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-semibold text-gray-900 drop-shadow-sm">Overall Progress</span>
            <span className="text-2xl font-bold text-[#4267A1] drop-shadow-sm">
              {project.progress}%
            </span>
          </div>
          <div className="w-full bg-white border border-gray-200 rounded-full h-4 overflow-hidden">
            <motion.div
              className="bg-gradient-to-r from-[#4267A1] via-[#5A7BC4] to-[#8A9150] h-4 rounded-full shadow-lg"
              initial={{ width: 0 }}
              animate={{ width: `${project.progress}%` }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Premium Next Steps */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center drop-shadow-sm">
            <Calendar className="w-5 h-5 mr-2 text-[#4267A1] drop-shadow-sm" />
            Next Steps
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tasks.filter(t => t.status !== 'completed').slice(0, 2).map((task) => (
              <motion.div 
                key={task.id} 
                whileHover={{ scale: 1.02 }}
                className="tyler-card p-4 border-l-4 border-[#4267A1]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1 drop-shadow-sm">{task.title}</h4>
                    <p className="text-sm text-gray-600 drop-shadow-sm">Due {new Date(task.dueDate).toLocaleDateString()}</p>
                  </div>
                  <div className="ml-4">
                    <div className="w-10 h-10 rounded-lg bg-[#4267A1]/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[#4267A1]" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Premium CTA */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={() => setShowSomaChat(true)}
            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-xl font-medium hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 flex items-center justify-center"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Ask SOMA
          </button>
          <button className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-xl font-medium hover:shadow-lg hover:shadow-[#8A9150]/25 transition-all duration-200 flex items-center justify-center">
            <FileText className="w-5 h-5 mr-2" />
            View Documentation
          </button>
        </div>
      </div>

      {/* Premium Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="tyler-card p-6 text-center">
          <div className="text-3xl font-bold text-[#8A9150] mb-2 drop-shadow-sm">{completedTasks.length}</div>
          <div className="text-sm text-gray-600 drop-shadow-sm">Completed</div>
        </div>
        <div className="tyler-card p-6 text-center">
          <div className="text-3xl font-bold text-[#4267A1] mb-2 drop-shadow-sm">{inProgressTasks.length}</div>
          <div className="text-sm text-gray-600 drop-shadow-sm">In Progress</div>
        </div>
        <div className="tyler-card p-6 text-center">
          <div className="text-3xl font-bold text-orange-600 mb-2 drop-shadow-sm">{overdueTasks.length}</div>
          <div className="text-sm text-gray-600 drop-shadow-sm">Overdue</div>
        </div>
        <div className="tyler-card p-6 text-center">
          <div className="text-3xl font-bold text-gray-900 mb-2 drop-shadow-sm">{tasks.length}</div>
          <div className="text-sm text-gray-600 drop-shadow-sm">Total Tasks</div>
        </div>
      </div>

      {/* Premium Task Checklist */}
      <div className="tyler-card p-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <CheckCircle className="w-6 h-6 mr-3 text-[#4267A1]" />
          Task Checklist
        </h3>
        <div className="space-y-4">
          {tasks.map((task) => (
            <motion.div
              key={task.id}
              whileHover={{ scale: 1.01 }}
              onClick={() => setSelectedTask(task)}
              className="flex items-center justify-between p-5 bg-white rounded-xl border border-gray-200 hover:border-[#4267A1]/50 transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center space-x-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTaskUpdate?.(task.id, task.status === 'completed' ? 'pending' : 'completed');
                  }}
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                    task.status === 'completed'
                      ? 'bg-gradient-to-r from-[#8A9150] to-[#A5B366] border-[#8A9150] shadow-lg shadow-[#8A9150]/25'
                      : 'border-gray-400 hover:border-[#4267A1] hover:bg-[#4267A1]/10'
                  }`}
                >
                  {task.status === 'completed' && <CheckCircle className="w-4 h-4 text-white" />}
                </button>
                <div className="flex-1">
                  <h4 className={`font-medium text-lg ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                    {task.title}
                  </h4>
                  <div className="flex items-center space-x-6 mt-2">
                    <span className="text-sm text-gray-600 flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Due: {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                    <span className="text-sm text-gray-600 flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {task.assignee}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1.5 text-xs font-medium rounded-full ${
                  task.status === 'completed' ? 'tyler-status-completed' :
                  task.status === 'in-progress' ? 'tyler-status-active' :
                  task.status === 'overdue' ? 'tyler-status-overdue' :
                  'tyler-status-pending'
                }`}>
                  {task.status.charAt(0).toUpperCase() + task.status.slice(1).replace('-', ' ')}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTask(task);
                  }}
                  className="text-gray-500 hover:text-[#4267A1] transition-colors p-2 rounded-lg hover:bg-[#4267A1]/10"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Updates */}
      <div className="tyler-card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Updates</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-[#8A9150] rounded-full mt-2"></div>
            <div>
              <p className="text-sm text-gray-900">Sample data file passed validation.</p>
              <p className="text-xs text-gray-600">SOMA • 10:14 AM</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-[#4267A1] rounded-full mt-2"></div>
            <div>
              <p className="text-sm text-gray-900">Training deck shared with team.</p>
              <p className="text-xs text-gray-600">Project Manager • Yesterday</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
            <div>
              <p className="text-sm text-gray-900">SSO configuration review scheduled.</p>
              <p className="text-xs text-gray-600">System • 2 days ago</p>
            </div>
          </div>
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Task Details</h3>
                <button 
                  onClick={() => setSelectedTask(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <h4 className="font-medium text-gray-900 mb-2">{selectedTask.title}</h4>
              <p className="text-gray-600 mb-4">{selectedTask.description}</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Assignee:</span>
                  <span className="text-sm text-gray-900">{selectedTask.assignee}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Due Date:</span>
                  <span className="text-sm text-gray-900">
                    {new Date(selectedTask.dueDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Status:</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(selectedTask.status)}`}>
                    {selectedTask.status.replace('-', ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Progress:</span>
                  <span className="text-sm text-gray-900">{selectedTask.completionPercentage}%</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button 
                onClick={() => setSelectedTask(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  if (onTaskUpdate) {
                    onTaskUpdate(selectedTask.id, 'completed');
                  }
                  setSelectedTask(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Mark Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOMA Chat Modal */}
      {showSomaChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 h-[80vh] flex flex-col">
            <SOMAChatInterface 
              onClose={() => setShowSomaChat(false)} 
              context="project"
              initialQuery=""
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientView;
